import React, { useState } from 'react';
import { useApi } from '../hooks/useApi.js';

const FALLBACK_ROLES = ['kitchen', 'front_of_house', 'cashier', 'manager', 'bar', 'delivery'];
const FALLBACK_SKILLS = ['food_prep', 'barista', 'wine_service', 'pos_system', 'cash_handling', 'cleaning', 'hosting', 'cocktails', 'baking'];
const EMPLOYMENT_TYPES = ['casual', 'part_time', 'full_time'];

/**
 * EmployeeModal – Add or edit an employee.
 *
 * Props:
 *   employee: object | null
 *   onClose(): void
 *   onSaved(employee): void
 */
export default function EmployeeModal({ employee, onClose, onSaved, roleNames, skillNames }) {
  const ROLES = roleNames?.length > 0 ? roleNames : FALLBACK_ROLES;
  const SKILLS_OPTIONS = skillNames?.length > 0 ? skillNames : FALLBACK_SKILLS;
  const api = useApi();
  const isEditing = !!employee;

  const [form, setForm] = useState({
    name: employee?.name || '',
    email: employee?.email || '',
    password: '',
    role: employee?.role || 'kitchen',
    employment_type: employee?.employment_type || 'casual',
    max_hours_per_week: employee?.max_hours_per_week ?? 38,
    min_hours_per_week: employee?.min_hours_per_week ?? 0,
    hourly_rate: employee?.hourly_rate || '',
    skills: employee?.skills || [],
    notes: employee?.notes || '',
    is_award_exempt: employee?.is_award_exempt || false,
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name === 'skills') {
      setForm(prev => ({
        ...prev,
        skills: checked
          ? [...prev.skills, value]
          : prev.skills.filter(s => s !== value)
      }));
    } else if (type === 'checkbox') {
      setForm(prev => ({ ...prev, [name]: checked }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
    setError('');
  }

  async function handleSave(e) {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) { setError('Name is required.'); return; }
    if (!form.role) { setError('Role is required.'); return; }
    if (!isEditing && form.email && !form.password) {
      setError('Password is required when email is provided.');
      return;
    }
    if (Number(form.min_hours_per_week) > Number(form.max_hours_per_week)) {
      setError('Min hours cannot exceed max hours.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : undefined,
        max_hours_per_week: parseInt(form.max_hours_per_week, 10),
        min_hours_per_week: parseInt(form.min_hours_per_week, 10),
      };

      // Don't send empty password on edit
      if (isEditing && !payload.password) delete payload.password;

      let saved;
      if (isEditing) {
        saved = await api.put(`/employees/${employee.id}`, payload);
      } else {
        saved = await api.post('/employees', payload);
      }
      onSaved(saved);
    } catch (err) {
      setError(err.message || 'Failed to save employee.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <span className="modal-title">{isEditing ? 'Edit Employee' : 'Add Employee'}</span>
          <button className="modal-close" onClick={onClose}>&#x2715;</button>
        </div>

        <form onSubmit={handleSave}>
          <div className="modal-body">
            {error && <div className="alert alert-error">{error}</div>}

            <div className="form-row">
              <div className="form-group">
                <label>Full Name <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Jane Smith"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="jane@example.com"
                />
                <div className="form-hint">Used for staff login</div>
              </div>
            </div>

            <div className="form-group">
              <label>{isEditing ? 'New Password' : 'Password'}{!isEditing && form.email ? <span className="required"> *</span> : ''}</label>
              <input
                type="password"
                className="form-control"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder={isEditing ? 'Leave blank to keep current' : 'Min 6 characters'}
                autoComplete="new-password"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Role <span className="required">*</span></label>
                <select className="form-control" name="role" value={form.role} onChange={handleChange} required>
                  {ROLES.map(r => (
                    <option key={r} value={r}>{r.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Employment Type</label>
                <select className="form-control" name="employment_type" value={form.employment_type} onChange={handleChange}>
                  {EMPLOYMENT_TYPES.map(t => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group" style={{ marginTop: -4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 'normal', minHeight: 36 }}>
                <input
                  type="checkbox"
                  name="is_award_exempt"
                  checked={form.is_award_exempt}
                  onChange={handleChange}
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                />
                Award-exempt (flat-rate / cash pay)
              </label>
              <div className="form-hint">Tick if this employee is paid a flat rate or cash — overtime warnings won't apply to them.</div>
            </div>

            <div className="form-row-3">
              <div className="form-group">
                <label>Hourly Rate ($)</label>
                <input
                  type="number"
                  className="form-control"
                  name="hourly_rate"
                  value={form.hourly_rate}
                  onChange={handleChange}
                  placeholder="25.00"
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>Min Hours/Week</label>
                <input
                  type="number"
                  className="form-control"
                  name="min_hours_per_week"
                  value={form.min_hours_per_week}
                  onChange={handleChange}
                  min="0" max="100"
                />
              </div>
              <div className="form-group">
                <label>Max Hours/Week</label>
                <input
                  type="number"
                  className="form-control"
                  name="max_hours_per_week"
                  value={form.max_hours_per_week}
                  onChange={handleChange}
                  min="1" max="100"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Skills</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: 4 }}>
                {SKILLS_OPTIONS.map(skill => (
                  <label key={skill} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, padding: '4px 0', minHeight: 32 }}>
                    <input
                      type="checkbox"
                      name="skills"
                      value={skill}
                      checked={form.skills.includes(skill)}
                      onChange={handleChange}
                      style={{ width: 16, height: 16, flexShrink: 0 }}
                    />
                    {skill.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Notes</label>
              <textarea
                className="form-control"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={2}
                placeholder="Any notes about this employee..."
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="spinner" /> : (isEditing ? 'Save Changes' : 'Add Employee')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
